import axios from "axios";

/**
 * ============================
 *   Config / Constantes
 * ============================
 */
const COOKIE_QUEUE_KEY = "mm_ref_queue";
const COOKIE_SID_KEY = "mm_ref_sid";
const COOKIE_CONSENT_KEY = "mm_consent"; // "accepted" | "denied"
const MAX_QUEUE_LENGTH = 200;
const MAX_COOKIE_BYTES = 3500; // ~3.5KB para ficar abaixo de 4KB
const SID_DAYS = 180; // 6 meses
const BASE = (process.env.VUE_APP_API_URL || "").replace(/\/+$/, "");
const PATH = "/api/referral-events/track";
const AMBIENT = process.env.VUE_APP_AMBIENT || process.env.VITE_AMBIENT || "";
const IS_PROD_LIKE = AMBIENT !== "DEV";
const COOKIE_PREFIX = IS_PROD_LIKE ? "__Secure-" : "";

function joinURL(base, path) {
  return `${base}${path.startsWith("/") ? path : `/${path}`}`;
}

function authHeaders() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function cname(baseName) {
  return `${COOKIE_PREFIX}${baseName}`;
}

function setCookieRaw(actualName, value, options = {}) {
  const {
    days,
    maxAgeSec,
    path = "/",
    sameSite = "Lax",
    // Em prod-like, sempre forçamos Secure=true. Em DEV, respeitamos o protocolo local.
    secure = IS_PROD_LIKE ? true : window.location.protocol === "https:",
  } = options;

  let cookie = `${encodeURIComponent(actualName)}=${encodeURIComponent(value)}`;
  if (typeof days === "number") {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    cookie += `; Expires=${date.toUTCString()}`;
  }
  if (typeof maxAgeSec === "number") cookie += `; Max-Age=${maxAgeSec}`;
  cookie += `; Path=${path}; SameSite=${sameSite}`;
  if (secure) cookie += `; Secure`;
  document.cookie = cookie;
}

function getCookieRaw(actualName) {
  const decoded = decodeURIComponent(document.cookie || "");
  const parts = decoded.split("; ");
  for (const part of parts) {
    const [k, ...rest] = part.split("=");
    if (k === encodeURIComponent(actualName) || k === actualName) {
      return rest.join("=");
    }
  }
  return null;
}

function getCookie(baseName) {
  const primary = getCookieRaw(cname(baseName));
  if (primary !== null) return primary;
  return getCookieRaw(baseName);
}

function setCookie(baseName, value, options = {}) {
  setCookieRaw(cname(baseName), value, options);
}

function deleteCookie(baseName) {
  setCookieRaw(cname(baseName), "", { days: -1 });
  setCookieRaw(baseName, "", { days: -1 });
}

export function getCookieConsentStatus() {
  const v = getCookie(COOKIE_CONSENT_KEY);
  if (v === "accepted") return "accepted";
  if (v === "denied") return "denied";
  return "unset";
}

export function hasCookieConsent() {
  return getCookieConsentStatus() === "accepted";
}

export function setCookieConsentAccepted() {
  setCookie(COOKIE_CONSENT_KEY, "accepted", { days: 365 });
}

export function setCookieConsentDenied() {
  setCookie(COOKIE_CONSENT_KEY, "denied", { days: 365 });
  clearQueueCookie(); // limpa fila ao negar
  deleteCookie(COOKIE_SID_KEY);
}

export function getOrCreateSessionId() {
  if (!hasCookieConsent()) return undefined; // só com consentimento
  let sid = getCookie(COOKIE_SID_KEY);
  if (!sid) {
    sid =
      (crypto?.randomUUID?.() || Math.random().toString(36).slice(2)) +
      Date.now();
    setCookie(COOKIE_SID_KEY, sid, { days: SID_DAYS });
  }
  return sid;
}

/**
 * ============================
 *   Fila em Cookie (compactada)
 * ============================
 * Item: { p: path, r: referrer_trunc, a, c, t }
 */
function bytesLen(str) {
  try {
    return new TextEncoder().encode(str).length;
  } catch {
    return unescape(encodeURIComponent(str)).length;
  }
}

function readQueueCookie() {
  try {
    const raw = getCookie(COOKIE_QUEUE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function writeQueueCookie(items) {
  let arr = Array.isArray(items) ? items.slice(-MAX_QUEUE_LENGTH) : [];
  let json = JSON.stringify(arr);
  while (bytesLen(json) > MAX_COOKIE_BYTES && arr.length > 1) {
    arr = arr.slice(1);
    json = JSON.stringify(arr);
  }
  setCookie(COOKIE_QUEUE_KEY, json, { days: SID_DAYS });
}

function clearQueueCookie() {
  deleteCookie(COOKIE_QUEUE_KEY);
}

/**
 * ============================
 *   Hash do User-Agent (apenas accepted)
 * ============================
 */
export async function computeUserAgentHash() {
  if (!hasCookieConsent()) return "";
  try {
    const ua = navigator.userAgent || "";
    const enc = new TextEncoder().encode(ua);
    const digest = await crypto.subtle.digest("SHA-256", enc);
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    return "";
  }
}

/**
 * ============================
 *   Builder do payload (garante campos)
 * ============================
 */
function buildPayload({ origin, item, sessionId, userAgentHash }) {
  return {
    landingUrl: origin + item.p, // sempre presente
    httpReferrer: item.r ?? null, // chave sempre presente
    sessionId: sessionId ?? "", // chave sempre presente
    userAgentHash: userAgentHash ?? "", // chave sempre presente
    a: item.a,
    c: item.c,
  };
}

/**
 * ============================
 *   Captura e Enfileira / Envia
 * ============================
 * Regras:
 *  - denied  → não salva, não envia
 *  - unset   → salva em cookie (fila), não envia
 *  - accepted→ salva em cookie e, se logado, envia (e flush no login)
 */
export async function handleReferralFromRoute({
  store,
  route,
  onlyQueue = false,
}) {
  const status = getCookieConsentStatus();
  if (status === "denied") return;

  const a = route.query?.a ? String(route.query.a) : undefined;
  const c = route.query?.c ? String(route.query.c) : undefined;
  if (!a && !c) return;

  const pathWithQuery = route.fullPath;
  const refShort = (document.referrer || "").slice(0, 256);

  const item = { p: pathWithQuery, r: refShort, a, c, t: Date.now() };

  // sempre guardamos em cookie quando status não é "denied"
  const q = readQueueCookie();
  q.push(item);
  writeQueueCookie(q);

  if (onlyQueue) return;

  // envio imediato só se consent = accepted e usuário logado
  if (status === "accepted" && store?.state?.user?.id) {
    try {
      const sid = getOrCreateSessionId();
      const uah = await computeUserAgentHash();
      const payload = buildPayload({
        origin: window.location.origin,
        item,
        sessionId: sid,
        userAgentHash: uah,
      });
      await axios.post(joinURL(BASE, PATH), payload, {
        headers: authHeaders(),
      });
    } catch {
      // silencioso
    }
    await flushReferralQueueAfterLogin();
  }
}

/**
 * Envia toda a fila após login (ou no beforeResolve) — apenas se consent = accepted.
 */
let _lastFlushAt = 0;
export async function flushReferralQueueAfterLogin() {
  if (!hasCookieConsent()) return;

  const now = Date.now();
  if (now - _lastFlushAt < 1000) return; // debounce
  _lastFlushAt = now;

  const queue = readQueueCookie();
  if (!queue.length) return;

  const sessionId = getOrCreateSessionId(); // sempre definido (accepted)
  const userAgentHash = await computeUserAgentHash();
  const origin = window.location.origin;

  const batchSize = 20;
  for (let i = 0; i < queue.length; i += batchSize) {
    const batch = queue.slice(i, i + batchSize);
    await Promise.all(
      batch.map((it) =>
        axios
          .post(
            joinURL(BASE, PATH),
            buildPayload({ origin, item: it, sessionId, userAgentHash }),
            { headers: authHeaders() }
          )
          .catch(() => null)
      )
    );
  }

  clearQueueCookie();
}
